import { Component, EventEmitter, inject, input, Input, OnInit, Output } from "@angular/core";
import { PayoutsService } from "@shared/services/payouts/payouts.service";
import { Payout, Transaction } from "src/app/shared/models/payouts.types";
import { takeUntil, map } from "rxjs/operators";
import { BehaviorSubject, Subject } from "rxjs";
import { MatLegacyDialog as MatDialog } from "@angular/material/legacy-dialog";
import { PaymentsApiService } from "../../../../services/payments-api.service";
import { PayMethodDetail } from "@modules/employee-data/types/accounts";
import { PermissionsService } from "@shared/services/permissions/permissions.service";
import { HistoryLogSidePanelService } from "@shared/components/history-log-side-panel/services/history-log-side-panel.service";
import { CommonService } from "@modules/dashboard/services/common.service";

@Component({
	selector: "app-payout-accounts-list",
	templateUrl: "./payout-accounts-list.component.html",
	styleUrls: ["./payout-accounts-list.component.scss"]
})
export class PayoutAccountsListComponent implements OnInit {
	@Input() employeeExternalId: string = "";
	@Input() employeeId: string = "";
	@Input() employeefirstName: string = "";
	@Input() employeelastName: string = "";
	@Input() payout!: Payout;
	@Input() activeToggle!: boolean;

	isCancelledRun: boolean = false;
	@Input() set _isCancelledRun(cancelled: boolean) {
		this.isCancelledRun = cancelled;
	}

	@Input() payoutTransactions!: Transaction[];

	selectedBulkRelevantStatuses: string[] = [];

	@Output() onShowEmployeeAction: EventEmitter<string> = new EventEmitter();

	payoutPayMethods!: PayMethodDetail[];
	payMethods: PayMethodDetail[] = [];

	reprocessIds: string[] = [];
	reProcesMapPayoutsAndTxId: Map<string, string[]> = new Map();

	isCheckBoxSelected: boolean = false;

	canViewTransactionHistory: boolean = false;
	canReprocessPayment: boolean = false;

	private destroy$: Subject<void> = new Subject();
	private paymentMethodsDataSource$: BehaviorSubject<PayMethodDetail[]> = new BehaviorSubject<PayMethodDetail[]>([]);
	private commonService = inject(CommonService);
	private payoutsService = inject(PayoutsService);
	public dialog = inject(MatDialog);
	private paymentApiService = inject(PaymentsApiService);
	private permissions = inject(PermissionsService);
	private historyLogService = inject(HistoryLogSidePanelService);

	bulkSelected$ = this.commonService.isBulkSelected$;
	bulkRelevantStatuses$ = this.commonService.bulkRelevantStatuses$;

	ngOnInit(): void {
		//Can view History
		this.permissions.canViewTransactionHistoryNETS().subscribe(res => {
			this.canViewTransactionHistory = res;
			this.getReprocessIds();
			this.loadPayMethods();
		});

		this.permissions
			.canReprocessPaymentsNETS()
			.pipe(takeUntil(this.destroy$))
			.subscribe(res => (this.canReprocessPayment = res));

		this.paymentApiService.reprocessTransactions().subscribe((res: boolean) => {
			if (res) {
				this.setData();
			}
		});

		this.bulkRelevantStatuses$.pipe(takeUntil(this.destroy$)).subscribe(statuses => {
			this.selectedBulkRelevantStatuses = statuses;
		});
	}

	getReprocessIds() {
		this.payoutsService
			.reprocessIdsObservable()
			.pipe(takeUntil(this.destroy$))
			.subscribe((res: Map<string, string[]>) => {
				this.reProcesMapPayoutsAndTxId = res;
				if (this.reProcesMapPayoutsAndTxId.has(this.payout.id)) {
					this.reprocessIds = this.payoutsService.getTransactionIdsFormMapPayouts(
						this.reProcesMapPayoutsAndTxId
					);
					this.isCheckBoxSelected = true;
				}
				if (this.reProcesMapPayoutsAndTxId.size === 0) this.isCheckBoxSelected = false;
			});
	}

	loadPayMethods() {
		this.payoutsService
			.getEmployeePaymentMethods(this.employeeId)
			.pipe(
				takeUntil(this.destroy$),
				map(res => {
					this.payMethods = res;
					this.paymentMethodsDataSource$.next(res);
					this.setData();
				})
			)
			.subscribe();
	}

	setData(): void {
		this.loadTransactionHistory(this.payout);
	}

	loadTransactionHistory(payout: Payout) {
		this.payoutTransactions.forEach((item, index) => {
			let bankName = this.payMethods.find(x => x.payMethod.accountId === item.accountId);
			let customPaymentField;
			customPaymentField = bankName?.details?.fields?.filter(
				field => field.key === "ACCOUNT_NUMBER" || field.key === "CARD_NUMBER"
			)[0];

			if (!customPaymentField?.value) {
				customPaymentField = bankName?.details?.fields?.filter(field => field.key === "IBAN")[0];
			}
			this.payoutTransactions[index] = { ...this.payoutTransactions[index], customPaymentField };
		});
	}

	goToEmployeeHistory(): void {
		this.onShowEmployeeAction.emit(this.payout.payCycleId);
	}

	openHistoryDialog(payoutId: string, paymethodId: string): void {
		this.historyLogService.setTransactionData(payoutId, paymethodId);
	}

	setIds(event: any, payoutId: string, transactionId: string) {
		if (event.checked) {
			this.isCheckBoxSelected = true;
			this.addTransactionsToReproces(payoutId, transactionId);
			this.payoutsService.updateReprocessIds(this.reProcesMapPayoutsAndTxId);
		} else {
			this.isCheckBoxSelected = false;
			this.deleteTransactionToReproces(payoutId, transactionId);
			this.payoutsService.updateReprocessIds(this.reProcesMapPayoutsAndTxId);
		}
	}

	addTransactionsToReproces(payoutid: string, transactionId: string): void {
		let txIds: string[] | undefined;

		if (this.reProcesMapPayoutsAndTxId.has(payoutid)) {
			txIds = this.reProcesMapPayoutsAndTxId.get(payoutid);

			if (txIds && !txIds.includes(transactionId)) {
				txIds.push(transactionId);
				this.reProcesMapPayoutsAndTxId.set(payoutid, txIds);
			}
		} else this.reProcesMapPayoutsAndTxId.set(payoutid, [transactionId]);
	}

	deleteTransactionToReproces(payoutid: string, transactionId: string): void {
		let txIds: string[] | undefined;
		if (this.reProcesMapPayoutsAndTxId.has(payoutid)) {
			txIds = this.reProcesMapPayoutsAndTxId.get(payoutid);
			if (txIds && txIds.length > 0) {
				let x: number = txIds?.indexOf(transactionId);
				if (x != -1) {
					txIds.splice(x, 1);
					if (txIds.length > 0) this.reProcesMapPayoutsAndTxId.set(payoutid, txIds);
					else this.reProcesMapPayoutsAndTxId.delete(payoutid);
				}
			} else this.reProcesMapPayoutsAndTxId.delete(payoutid);
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
